import { isTen } from './cards';
import type { DealerPolicy, RulesConfig } from './rules';
import type { HandResult, HandState, Seat, Team } from './types';
import { teamOf } from './types';

/**
 * PRD §4.4 — tens first, tricks second. A tie in both is a draw (only possible with an even trick
 * count, e.g. DOUBLE_6 or the legacy single-deck presets): no points, redeal by the same dealer.
 */
export function scoreHand(
  hand: HandState,
  rules: RulesConfig,
  totalTens: number,
  totalTricks: number,
): Omit<HandResult, 'nextDealer'> {
  const tens: [number, number] = [
    hand.captured[0].filter(isTen).length,
    hand.captured[1].filter(isTen).length,
  ];
  const tricks: [number, number] = [hand.tricksWon[0], hand.tricksWon[1]];
  let winnerTeam: Team | null = null;
  if (tens[0] !== tens[1]) winnerTeam = tens[0] > tens[1] ? 0 : 1;
  else if (tricks[0] !== tricks[1]) winnerTeam = tricks[0] > tricks[1] ? 0 : 1;

  const coat = winnerTeam !== null && tens[winnerTeam] === totalTens;
  const whitewash = winnerTeam !== null && tricks[winnerTeam] === totalTricks;
  let points = 0;
  if (winnerTeam !== null) {
    points = rules.scoreModel === 'APP_POINTS' ? (whitewash ? 3 : coat ? 2 : 1) : 1;
  }
  return {
    handNumber: hand.handNumber,
    handId: hand.handId,
    dealer: hand.dealer,
    winnerTeam,
    draw: winnerTeam === null,
    tens,
    tricks,
    coat,
    whitewash,
    points,
    trumpSuit: hand.trump.suit,
  };
}

/**
 * Classic loser-deals tradition (PRD §4.4): dealer's team wins → deal passes one seat right; loses →
 * dealer repeats; whitewash loss → dealer's partner (next teammate in play order) deals.
 */
export function nextDealer(
  policy: DealerPolicy,
  dealer: Seat,
  players: number,
  result: Pick<HandResult, 'winnerTeam' | 'whitewash'>,
): Seat {
  if (result.winnerTeam === null) return dealer;
  if (policy === 'ROTATE') return (dealer + 1) % players;
  if (result.winnerTeam === teamOf(dealer)) return (dealer + 1) % players;
  if (result.whitewash) return (dealer + 2) % players;
  return dealer;
}
